import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Plus, Trash } from "@phosphor-icons/react";

const FIELDS = {
  jiwa: ["policy_age_months", "claim_amount", "cause_of_death", "previous_claims_count", "report_delay_days", "documents_complete", "insured_age"],
  kendaraan: ["policy_age_months", "claim_ratio", "claim_amount", "police_report", "is_night", "previous_claims_count", "report_delay_days", "documents_complete", "witness_present", "incident_hour", "vehicle_year"],
};
const OPERATORS = { eq: "= (sama dengan)", neq: "≠ (tidak sama)", lt: "< (kurang dari)", lte: "≤", gt: "> (lebih dari)", gte: "≥", between: "antara [min,maks)", in: "termasuk dalam daftar" };

const parseValue = (raw, operator) => {
  const s = raw.trim();
  if (operator === "between") return s.split(",").map((x) => Number(x.trim()));
  if (operator === "in") return s.split(",").map((x) => x.trim());
  if (s === "true") return true;
  if (s === "false") return false;
  return isNaN(Number(s)) ? s : Number(s);
};
const showValue = (v) => Array.isArray(v) ? v.join(", ") : String(v);

const emptyForm = { claim_type: "jiwa", name: "", description: "", field: "", operator: "gt", value: "", points: 10 };

export default function RulesTab() {
  const [rules, setRules] = useState([]);
  const [typeFilter, setTypeFilter] = useState("jiwa");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [pointsDraft, setPointsDraft] = useState({});

  const load = () => api.get("/admin/rules").then((r) => setRules(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const update = async (id, updates) => {
    try {
      await api.put(`/admin/rules/${id}`, updates);
      toast.success("Aturan diperbarui — berlaku untuk klaim baru");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/rules/${id}`); toast.success("Aturan dihapus"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const create = async () => {
    try {
      await api.post("/admin/rules", { ...form, points: Number(form.points), value: parseValue(form.value, form.operator) });
      toast.success("Aturan baru ditambahkan");
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = rules.filter((r) => r.claim_type === typeFilter);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="rules-type-filter" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="jiwa">Asuransi Jiwa</SelectItem>
            <SelectItem value="kendaraan">Asuransi Kendaraan</SelectItem>
          </SelectContent>
        </Select>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-rule-button" className="bg-slate-900 hover:bg-slate-800 rounded-none gap-2"><Plus size={16} /> Tambah Aturan</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Aturan Scoring Baru</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Jenis Klaim</Label>
                  <Select value={form.claim_type} onValueChange={(v) => setForm((s) => ({ ...s, claim_type: v, field: "" }))}>
                    <SelectTrigger data-testid="rule-form-type"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="jiwa">Jiwa</SelectItem><SelectItem value="kendaraan">Kendaraan</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Poin</Label>
                  <Input data-testid="rule-form-points" type="number" min="1" max="100" value={form.points} onChange={(e) => setForm((s) => ({ ...s, points: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Nama Aturan</Label>
                <Input data-testid="rule-form-name" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="Contoh: Klaim di provinsi rawan" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Deskripsi</Label>
                <Textarea rows={2} value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">Field Data</Label>
                  <Select value={form.field} onValueChange={(v) => setForm((s) => ({ ...s, field: v }))}>
                    <SelectTrigger data-testid="rule-form-field"><SelectValue placeholder="Pilih field" /></SelectTrigger>
                    <SelectContent>{FIELDS[form.claim_type].map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Operator</Label>
                  <Select value={form.operator} onValueChange={(v) => setForm((s) => ({ ...s, operator: v }))}>
                    <SelectTrigger data-testid="rule-form-operator"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(OPERATORS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Nilai Pembanding</Label>
                <Input data-testid="rule-form-value" value={form.value} onChange={(e) => setForm((s) => ({ ...s, value: e.target.value }))}
                  placeholder={form.operator === "between" ? "min, maks — contoh: 6, 12" : form.operator === "in" ? "pisahkan koma — contoh: kecelakaan, tidak_diketahui" : "contoh: 6 / true / false"} />
              </div>
              <Button data-testid="rule-form-submit" onClick={create} disabled={!form.name || !form.field || form.value === ""}
                className="w-full bg-slate-900 hover:bg-slate-800 rounded-none">Simpan Aturan</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-card border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="px-4 py-3 font-medium">Aturan</th>
              <th className="px-3 py-3 font-medium">Kondisi</th>
              <th className="px-3 py-3 font-medium w-24">Poin</th>
              <th className="px-3 py-3 font-medium w-20">Aktif</th>
              <th className="px-3 py-3 w-12"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id} data-testid={`rule-row-${r.id}`} className="border-b border-border/60 last:border-0">
                <td className="px-4 py-3">
                  <p className="font-medium">{r.name}</p>
                  <p className="text-xs text-muted-foreground">{r.description}</p>
                </td>
                <td className="px-3 py-3 font-mono text-xs">{r.field} {r.operator} {showValue(r.value)}</td>
                <td className="px-3 py-3">
                  <Input data-testid={`rule-points-${r.id}`} type="number" min="1" max="100" className="w-20 h-8"
                    value={pointsDraft[r.id] ?? r.points}
                    onChange={(e) => setPointsDraft((s) => ({ ...s, [r.id]: e.target.value }))}
                    onBlur={(e) => { const v = Number(e.target.value); if (v && v !== r.points) update(r.id, { points: v }); }} />
                </td>
                <td className="px-3 py-3">
                  <Switch data-testid={`rule-active-${r.id}`} checked={r.active} onCheckedChange={(v) => update(r.id, { active: v })} />
                </td>
                <td className="px-3 py-3">
                  <button data-testid={`rule-delete-${r.id}`} onClick={() => remove(r.id)} className="text-muted-foreground hover:text-red-600 transition-colors duration-150"><Trash size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
